import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { FileXMarkIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import TextInput from '../../../../../../packages/ui/components/atoms/input/text-input'
import { cancelEnrollmentRequest } from '../../../api/enrollments/send-cancel-journey-email'
import LabelWithText from '../../../components/elements/label-with-text'
import PeriodBadge from '../../../components/elements/period-badge'
import ClientSpacePageTitle from '../../../components/pages/client-space/client-space-page-title'
import useEnrollment from '../../../lib/hooks/use-enrollment'
import { EnrollmentCancellationStatusEnum } from '../../../lib/types/enums/enrollment/enrollment-cancellation-status.enum'
import { InsuranceTypeEnum } from '../../../lib/types/enums/insurance/insurance-type.enum'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'
import { InsuranceProperties } from '../../../lib/types/models/insurance/properties.insurance'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'

const ClientCancelJourneyPage = () => {
  const { enrollment, setEnrollment } = useEnrollment()
  const [value, setValue] = useState(enrollment?.parentCancellationReason || '')
  // a One to One Individuel enrollment has no journey; its own période carries no localized name,
  // and the badges below are labelled explicitly anyway
  const period = EnrollmentProperties.isOtoIndividual(enrollment)
    ? undefined
    : enrollment?.mainProposition?.journey.date.period

  const { mutateAsync: cancelRequest, isPending } = useMutation({
    mutationFn: () => cancelEnrollmentRequest(enrollment.id, { reason: value }),
    onSuccess: () => {
      setEnrollment({
        ...enrollment,
        parentCancellationReason: value,
        parentCancellationStatus: EnrollmentCancellationStatusEnum.CANCELED_BY_PARENT
      })
      toast.success('Votre demande d’annulation a bien été prise en compte.')
    },
    onError: showAxiosError
  })

  useEffect(() => {
    if (enrollment?.parentCancellationReason) setValue(enrollment.parentCancellationReason)
  }, [enrollment?.parentCancellationReason])

  const hasCancellationInsurance = InsuranceProperties.hasEnrollmentInsuranceType(
    EnrollmentProperties.isOtoIndividual(enrollment)
      ? enrollment?.otoIndividualProposition?.insurances
      : enrollment?.mainProposition?.insurances,
    InsuranceTypeEnum.CANCELLATION
  )

  const isEnrollmentCancelled =
    enrollment?.parentCancellationStatus === EnrollmentCancellationStatusEnum.CANCELED_BY_PARENT

  return (
    <div className="flex flex-col gap-4 mobile:mb-10">
      <ClientSpacePageTitle
        icon={<FileXMarkIcon color={tailwindColors.primary.dark.DEFAULT} />}
        title="Annuler le séjour"
        subTitlePosition="above"
        subtitle={
          isEnrollmentCancelled ? (
            <Badge
              size="medium"
              className="w-full h-11 mobile:h-fit mobile:py-3"
              variant="success"
              text="Nous avons pris en compte votre demande d'annulation pour ce séjour. Notre équipe reviendra vers vous prochainement."
            />
          ) : undefined
        }
      />
      <InnerBox className="rounded-xl p-4 flex flex-col gap-4">
        <InnerBox className="rounded-xl p-4 grid grid-cols-5 items-center bg-white-hover mobile:grid-cols-1 mobile:gap-2">
          <LabelWithText
            label={'Participant'}
            data={toFullName(enrollment.childFirstName, enrollment.childLastName, {
              options: { mode: 'lastname-firstname' }
            })}
          />
          <LabelWithText
            className="col-span-4"
            label={'Séjour'}
            data={
              <div className="flex gap-1 items-center mobile:flex-col mobile:items-start">
                <PeriodBadge
                  period={period}
                  className="mobile:h-fit mobile:py-1"
                  text={EnrollmentProperties.getEnrollmenJourneytDetails(enrollment)}
                />
                <PeriodBadge
                  period={period}
                  text={EnrollmentProperties.getEnrollmentMainTripDetails(enrollment)}
                />
              </div>
            }
          />
        </InnerBox>

        <InnerBox
          className={cn('rounded-xl p-4 ', {
            'bg-white-hover': isEnrollmentCancelled
          })}
        >
          <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
            Motif(s) d'annulation *
          </Typography>
          <div className="h-2" />

          {isEnrollmentCancelled ? (
            <Typography variant="tag" color={tailwindColors.grey.lighter}>
              {enrollment.parentCancellationReason}
            </Typography>
          ) : (
            <>
              <TextInput
                placeholder="Merci d’indiquer précisément le motif de l’annulation du séjour de votre enfant"
                type="textarea"
                maxLength={500}
                value={value}
                rows={8}
                onChange={(e) => setValue(e.target.value)}
              />
              <div className="h-2" />
              <div className="flex items-start justify-between mobile:flex-col mobile:items-start mobile:gap-2">
                <Typography variant="small" color={tailwindColors.grey.DEFAULT}>
                  ({value.length}/{500})
                </Typography>
                <Button
                  variant="destructive"
                  disabled={!value.trim()}
                  className="mobile:w-full"
                  label="Annuler mon contrat"
                  loading={isPending}
                  onClick={() => {
                    if (!value.trim()) return
                    cancelRequest()
                  }}
                />
              </div>
            </>
          )}
        </InnerBox>
        {!isEnrollmentCancelled && (
          <div className="flex items-center gap-2 mobile:flex-col mobile:items-start ">
            <Badge.Link
              iconPosition="start"
              color={tailwindColors.white.hover}
              borderColor={tailwindColors.grey.light.DEFAULT}
              text="Rappel des conditions d'annulation"
              size="small"
              onClick={() =>
                window.open(
                  'https://www.sejour-linguistique-lec.fr/pdf/cgv-lec-2026.pdf',
                  '_blank'
                )
              }
              textColor={tailwindColors.primary.DEFAULT}
              style={{ textDecorationColor: tailwindColors.primary.DEFAULT }}
            />
            {hasCancellationInsurance && (
              <Badge.Link
                iconPosition="start"
                color={tailwindColors.white.hover}
                borderColor={tailwindColors.grey.light.DEFAULT}
                text="Rappel des conditions de l'assurance annulation"
                size="small"
                className="mobile:h-fit"
                onClick={() =>
                  window.open(
                    'https://www.sejour-linguistique-lec.fr/pdf/cgv-lec-2026.pdf',
                    '_blank'
                  )
                }
                textColor={tailwindColors.primary.DEFAULT}
                style={{ textDecorationColor: tailwindColors.primary.DEFAULT }}
              />
            )}
          </div>
        )}
      </InnerBox>
    </div>
  )
}

export default ClientCancelJourneyPage
